import { useEffect, useState } from 'react'

import { useSelector } from 'react-redux'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

import './App.css'
import { env } from '@/core/environments'
import { usePartnerCustomization } from '@/core/hooks/misc/usePartnerCustomization'
import { useAuth } from '@/core/hooks/useAuth'
import { usePermission } from '@/core/hooks/usePermission'
import { RootState } from '@/core/store'
import AccountPage from '@/features/account/AccountPage'
import { ForgotPassword } from '@/features/auth/forgotPassword/ForgotPassword'
import { ResetPasswordPage } from '@/features/auth/forgotPassword/ResetPasswordPage'
import { LoginPage } from '@/features/auth/login/LoginPage'
import { LoginAsUserPage } from '@/features/auth/loginAsUser/LoginAsUserPage'
import { Register } from '@/features/auth/register/Register'
import { SetupUserPage } from '@/features/auth/setupUser/SetupUserPage'
import CampaignDetailsPage from '@/features/campaigns/CampaignDetailsPage'
import CampaignDetailsStep2 from '@/features/campaigns/CampaignDetailsStep2'
import { CampaignFlowLayout } from '@/features/campaigns/CampaignFlowLayout'
import CampaignListPage from '@/features/campaigns/CampaignListPage'
import CreateAICampaignPage from '@/features/campaigns/CreateAICampaignPage'
import IntegrationsPage from '@/features/integrations/IntegrationsPage'
import MusicPage from '@/features/music/MusicPage'
import PlaylistDetailsPage from '@/features/music/PlaylistDetailsPage'
import ViewAllPlaylistsPage from '@/features/music/ViewAllPlaylistsPage'
import ScheduleDetailsPage from '@/features/schedule/ScheduleDetailsPage'
import SchedulesListPage from '@/features/schedule/SchedulesListPage'
import ViewAllSchedulesPage from '@/features/schedule/ViewAllSchedulesPage'
import BillingInformationPage from '@/features/subscribe/BillingInformationPage'
import ChooseSubscriptionPage from '@/features/subscribe/ChooseSubscriptionPage'
import PaymentPage from '@/features/subscribe/PaymentPage'
import Create from '@/features/zones/Create/Create'
import GroupDetails from '@/features/zones/Details/group/GroupDetails'
import LocationDetails from '@/features/zones/Details/location/LocationDetails'
import LocationMonitor from '@/features/zones/Details/location/LocationMonitor'
import ZoneDetails from '@/features/zones/Details/zone/ZoneDetails'
import Zones from '@/features/zones/List/Zones'

import { ProtectedRoute } from '@shared/components/atoms/ProtectedRoute'
import { SchedulesFeatureGuard } from '@shared/components/guards/SchedulesFeatureGuard'

import { ThemePreview } from './dev/ThemePreview'
import { ChatbaseWidget } from './shared/analytics/ChatbaseWidget'

const RegisterRedirect = () => {
	const location = useLocation()
	return <Navigate to={`/register/step/0${location.search || ''}`} replace />
}

const ZoneCreateGuard = ({ children }: { children: React.ReactElement }) => {
	const { isAuthenticated } = useAuth()
	const { features } = usePermission()

	// Only check permission if user is authenticated
	if (isAuthenticated && !features.zone.create) {
		return <Navigate to="/zones" replace />
	}

	return children
}

const CampaignCreateGuard = ({
	children,
}: {
	children: React.ReactElement
}) => {
	const { isAuthenticated } = useAuth()
	const { features } = usePermission()

	if (isAuthenticated && !features.campaign.create) {
		return <Navigate to="/campaigns" replace />
	}

	return children
}

const CampaignEditGuard = ({ children }: { children: React.ReactElement }) => {
	const { isAuthenticated } = useAuth()
	const { features } = usePermission()

	if (isAuthenticated && !features.campaign.edit) {
		return <Navigate to="/campaigns" replace />
	}

	return children
}

const CampaignCreateAiGuard = ({
	children,
}: {
	children: React.ReactElement
}) => {
	const { isAuthenticated } = useAuth()
	const { features } = usePermission()

	if (
		isAuthenticated &&
		(!features.campaign.create || !features.campaign.createAi)
	) {
		return <Navigate to="/campaigns" replace />
	}

	return children
}

const IntegrationsGuard = ({ children }: { children: React.ReactElement }) => {
	const { isAuthenticated } = useAuth()
	const { features } = usePermission()

	if (isAuthenticated && !features.integrations.enabled) {
		return <Navigate to="/zones" replace />
	}

	return children
}

const GroupGuard = ({ children }: { children: React.ReactElement }) => {
	const { isAuthenticated } = useAuth()
	const { features } = usePermission()

	if (isAuthenticated && !features.group.enabled) {
		return <Navigate to="/zones" replace />
	}

	return children
}

const isInAuthFlow = (pathname: string) => {
	const prefixes = ['/login', '/register', '/forgot-password', '/setup-user']
	return prefixes.some(
		(prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
	)
}

export function App() {
	const { partnerName } = usePartnerCustomization()
	const location = useLocation()
	const { isAuthenticated } = useSelector((state: RootState) => state.auth)
	const [isMobile, setIsMobile] = useState(() =>
		typeof window !== 'undefined'
			? window.matchMedia('(max-width: 1023px)').matches
			: false
	)

	useEffect(() => {
		const mq = window.matchMedia('(max-width: 1023px)')
		const update = () => setIsMobile(mq.matches)
		mq.addEventListener('change', update)
		return () => mq.removeEventListener('change', update)
	}, [])

	// Only show Chatbase once logged in AND not during auth/signup flows.
	// On mobile, we only mount it inside /account to avoid overlapping the bottom navbar.
	const chatbaseEnabled =
		isAuthenticated &&
		!isInAuthFlow(location.pathname) &&
		(!isMobile || location.pathname.startsWith('/account'))

	// Update document title based on custom settings or environment
	useEffect(() => {
		document.title = partnerName || env.APP_NAME
	}, [partnerName])

	return (
		<SchedulesFeatureGuard>
			<ChatbaseWidget enabled={chatbaseEnabled} />
			<Routes>
				{/* Public routes (for non-authenticated users) */}
				<Route
					path="/login"
					element={
						<ProtectedRoute requireAuth={false} redirectTo="/zones">
							<LoginPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/login/google"
					element={
						<ProtectedRoute requireAuth={false} redirectTo="/zones">
							<LoginPage />
						</ProtectedRoute>
					}
				/>

				<Route path="/forgot-password" element={<ForgotPassword />} />

				<Route
					path="/forgot-password/:email/:token"
					element={<ResetPasswordPage />}
				/>

				<Route
					path="/register"
					element={
						<ProtectedRoute requireAuth={false} redirectTo="/zones">
							<RegisterRedirect />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/register/step/:step"
					element={
						<ProtectedRoute requireAuth={false} redirectTo="/zones">
							<Register />
						</ProtectedRoute>
					}
				/>

				<Route path="/setup-user/:email/:token" element={<SetupUserPage />} />

				{/* Protected routes (require authentication) */}
				{/* Dashboard route - currently not in use */}
				{/* <Route
				path="/dashboard"
				element={
					<ProtectedRoute>
						<LayoutExample />
					</ProtectedRoute>
				}
			/> */}

				{/* Guide route - only accessible during trial */}
				{/* <Route
					path="/guide"
					element={
						<ProtectedRoute>
							<TrialGuard>
								<GuidePage />
							</TrialGuard>
						</ProtectedRoute>
					}
				/> */}

				{/* Zones routes */}
				<Route
					path="/zones"
					element={
						<ProtectedRoute>
							<Zones />
						</ProtectedRoute>
					}
				/>

				{/* Zone Create route */}
				<Route
					path="/zones/create"
					element={
						<ProtectedRoute>
							<ZoneCreateGuard>
								<Create />
							</ZoneCreateGuard>
						</ProtectedRoute>
					}
				/>

				{/* Zone Details route */}
				<Route
					path="/zones/:zoneId"
					element={
						<ProtectedRoute>
							<ZoneDetails />
						</ProtectedRoute>
					}
				/>

				{/* Public shared Location Details route (UUID) */}
				<Route path="/locations/:uuid/details" element={<LocationMonitor />} />

				{/* Location Details route */}
				<Route
					path="/locations/:locationId"
					element={
						<ProtectedRoute>
							<LocationDetails />
						</ProtectedRoute>
					}
				/>

				{/* Group Management routes */}
				<Route
					path="/groups/:id"
					element={
						<ProtectedRoute>
							<GroupGuard>
								<GroupDetails />
							</GroupGuard>
						</ProtectedRoute>
					}
				/>

				{/* Music route */}
				<Route
					path="/music"
					element={
						<ProtectedRoute>
							<MusicPage />
						</ProtectedRoute>
					}
				/>

				{/* Playlist Details route - must come before /music/:type */}
				<Route
					path="/music/playlists/:playlistId"
					element={
						<ProtectedRoute>
							<PlaylistDetailsPage />
						</ProtectedRoute>
					}
				/>

				{/* View All Playlists route */}
				<Route
					path="/music/:type"
					element={
						<ProtectedRoute>
							<ViewAllPlaylistsPage />
						</ProtectedRoute>
					}
				/>

				{/* Schedules routes */}
				<Route
					path="/schedules"
					element={
						<ProtectedRoute>
							<SchedulesListPage />
						</ProtectedRoute>
					}
				/>

				{/* View All Schedules route - must come before /schedules/:id */}
				<Route
					path="/schedules/view/:type"
					element={
						<ProtectedRoute>
							<ViewAllSchedulesPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/schedules/:id"
					element={
						<ProtectedRoute>
							<ScheduleDetailsPage />
						</ProtectedRoute>
					}
				/>

				{/* Integrations route */}
				<Route
					path="/integrations"
					element={
						<ProtectedRoute>
							<IntegrationsGuard>
								<IntegrationsPage />
							</IntegrationsGuard>
						</ProtectedRoute>
					}
				/>

				{/* Campaigns routes */}
				<Route
					path="/campaigns"
					element={
						<ProtectedRoute>
							<CampaignListPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/campaigns/:id"
					element={
						<ProtectedRoute>
							<CampaignDetailsPage readOnly={true} />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/campaigns/create"
					element={
						<ProtectedRoute>
							<CampaignCreateGuard>
								<CampaignFlowLayout />
							</CampaignCreateGuard>
						</ProtectedRoute>
					}
				>
					<Route index element={<CampaignDetailsPage />} />
					<Route path="upload" element={<CampaignDetailsStep2 />} />
				</Route>

				<Route
					path="/campaigns/:id/edit"
					element={
						<ProtectedRoute>
							<CampaignEditGuard>
								<CampaignFlowLayout />
							</CampaignEditGuard>
						</ProtectedRoute>
					}
				>
					<Route index element={<CampaignDetailsPage />} />
					<Route path="upload" element={<CampaignDetailsStep2 />} />
				</Route>

				<Route
					path="/campaigns/create-ai"
					element={
						<ProtectedRoute>
							<CampaignCreateAiGuard>
								<CreateAICampaignPage />
							</CampaignCreateAiGuard>
						</ProtectedRoute>
					}
				/>

				{/* Account route */}
				<Route
					path="/account"
					element={
						<ProtectedRoute>
							<AccountPage />
						</ProtectedRoute>
					}
				/>

				{/* Subscribe routes */}
				<Route
					path="/subscribe"
					element={
						<ProtectedRoute>
							<ChooseSubscriptionPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/subscribe/billing"
					element={
						<ProtectedRoute>
							<BillingInformationPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/subscribe/payment"
					element={
						<ProtectedRoute>
							<PaymentPage />
						</ProtectedRoute>
					}
				/>

				{/* Login as user route (protected - requires authentication to login as another user) */}
				<Route path="/login/token/:token" element={<LoginAsUserPage />} />

				{/* Redirect root to zones */}
				<Route path="/" element={<Navigate to="/zones" replace />} />

				{/* DEV: Theme preview route */}
				<Route path="/theme-preview" element={<ThemePreview />} />

				{/* Fallback for unmatched routes */}
				<Route path="*" element={<Navigate to="/login" replace />} />
			</Routes>
		</SchedulesFeatureGuard>
	)
}
